import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import constate from '../../common/lib/constate';
import Helper from '../utils/Helper';
import CmsAPI from '../../common/apis/CmsAPI';
import jwtDecode from 'jwt-decode';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { ToastNotification } from '../components/Toast/Toast';
import { parseString } from '../utils/stringHelper';
import VormatsAPI from '../../common/apis/VormatsAPI';
import FirebaseFeature from '../utils/firebase';
import { logEvent } from 'firebase/analytics';
import configs from '../../../conf';
import { useHistory } from 'react-router-dom';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { APP_USER_ROLE } from '../constants/UserRoles';
import * as Sentry from '@sentry/react';

const config = configs[process.env.NODE_ENV];

const initialNotificationsSettings = {
  mediumSettings: [],
  settings: [],
};

function useUser() {
  const [user, setUser] = useState(null);
  const [userLoading, setUserLoading] = useState(true);
  const { t } = useTranslation();
  const [appConfig, setAppConfig] = useState(null);
  const [notLoggedIn, setNotLoggedIn] = useState(false);

  const [isApprovalFlowEnabled, setIsApprovalFlowEnabled] = useState(false);
  const [isAppcuesEnabled, setIsAppcuesEnabled] = useState(false);

  const [isReviewerUser, setIsReviewerUser] = useState(false);
  const mediaMatch = window.matchMedia('(max-width: 900px)');
  const [isMobileView] = useState(mediaMatch.matches);

  const [userCompanyId, setUserCompanyId] = useState(null);
  const [userFeatures, setUserFeatures] = useState([]);
  const hasForcedWebAccessLogout = useRef(false);

  const [defaultSettings, setDefaultSettings] = useState([]);
  const [notificationSettings, setNotificationSettings] = useState(initialNotificationsSettings);

  const [isWebInvitationEnabled, setIsWebInvitationEnabled] = useState(false);
  const [isStoryboardGeneratorEnabled, setIsStoryboardGeneratorEnabled] = useState(false);
  const [isSceneSpeedEnabled, setIsSceneSpeedEnabled] = useState(false);
  const [isInviteCreateUser, setIsInviteCreateUser] = useState(false);

  const [isFirebaseEnabled, setIsFirebaseEnabled] = useState(false);

  const [isCallToActionEnabled, setIsCallToActionEnabled] = useState(false);
  const [isSentryEnabled, setIsSentryEnabled] = useState(false);
  const [isZoomFeatureEnabled, setIsZoomFeatureEnabled] = useState(false);
  const [isUserManagementEnabled, setIsUserManagementEnabled] = useState(false);
  const [isViewAnalyticsEnabled, setIsViewAnalyticsEnabled] = useState(false);
  const [isLogoManagementEnabled, setIsLogoManagementEnabled] = useState(false);
  const [isTextPanelManagementEnabled, setIsTextPanelManagementEnabled] = useState(false);
  const [isVideoTagsEnabled, setIsVideoTagsEnabled] = useState(false);
  const [isSSOAuthenticationEnabled, setIsSSOAuthenticationEnabled] = useState(false);
  const [isProductTrainingEnabled, setIsProductTrainingEnabled] = useState(false);
  const [isStoryboardScriptPromptsV2Enabled, setIsStoryboardScriptPromptsV2Enabled] = useState(false);
  const [isProfessionalRequestEnabled, setIsProfessionalRequestEnabled] = useState(false);
  const [isMinutesModelEnabled, setIsMinutesModelEnabled] = useState(false);
  const [isStoryboardAssistantEnabled, setIsStoryboardAssistantEnabled] = useState(false);
  const [isTypebotEnabled, setIsTypebotEnabled] = useState(false);
  const [isVoiceoverEnabled, setIsVoiceoverEnabled] = useState(false);
  const [companyTags, setCompanyTags] = useState([]);
  const [isAudioEnhanceEnabled, setIsAudioEnhanceEnabled] = useState(false);
  const [isAvatarEnabled, setIsAvatarEnabled] = useState(false);
  const [isCustomColorsEnabled, setIsCustomColorsEnabled] = useState(false);
  const [isContentScheduleEnabled, setIsContentScheduleEnabled] = useState(false);
  const [isVormatsPro, setIsVormatsPro] = useState(false);
  const [isImproveSubtitlesEnabled, setIsImproveSubtitlesEnabled] = useState(false);
  const [isGenerateVoiceoverEnabled, setIsGenerateVoiceoverEnabled] = useState(false);
  const [isAdvancedEditorButtonEnabled, setIsAdvancedEditorButtonEnabled] = useState(false);
  const [isScriptAssistantAttachmentsEnabled, setIsScriptAssistantAttachmentsEnabled] = useState(false);
  const [isEditAutomaticallyStoryboardEnabled, setIsEditAutomaticallyStoryboardEnabled] = useState(false);
  const [isWhatToShowVisibleWebEnabled, setIsWhatToShowVisibleWebEnabled] = useState(false);

  const [isNewGenerationFlow, setIsNewGenerationFlow] = useState(false);
  const [newWebAppDesign, setNewWebAppDesign] = useState(false);

  const [fonts, setFonts] = useState([]);
  const [userRole, setUserRole] = useState();

  const isSuperAdmin = useMemo(() => userRole === APP_USER_ROLE.SUPER_ADMIN.TYPE, [userRole]);
  const isExternalVideoEditor = useMemo(() => userRole === APP_USER_ROLE.VIDEO_EDITOR.TYPE, [userRole]);
  const isWebAccessDisabled = useMemo(() => {
    const webAccessUserFeature = userFeatures.find(
      feature => feature.feature?.publicName === 'web_access' || feature.feature?.name === 'web_access'
    );
    if (webAccessUserFeature) {
      return !webAccessUserFeature.enabled;
    }

    const webAccessAppConfigFeature = appConfig?.features?.find(feature => feature.name === 'web_access');
    if (webAccessAppConfigFeature) {
      return !webAccessAppConfigFeature.enabled;
    }

    return false;
  }, [appConfig, userFeatures]);

  const history = useHistory();
  const { pathname } = useLocation();

  const updateTags = async tags => {
    if (tags) {
      setCompanyTags(tags);
      return;
    }

    const loadedTags = await VormatsAPI.getCompanyTags();
    setCompanyTags(loadedTags);
  };

  const initializeFirebase = () => {
    FirebaseFeature.initializeFirebase();
  }

  const getAppConfig = async userData => {
    try {
      const fonts = await VormatsAPI.getFontsList();
      setFonts(fonts);
      updateTags();
    } catch (err) {
      Sentry.captureException(err);
      console.log('Error while getting default fonts');
    }

    let userId = null;
    let companyId = null;
    if (user !== null) {
      try {
        const result = await VormatsAPI.getUserWithRole();
        setUserRole(result.role?.id);
        setIsReviewerUser(result.role?.id === APP_USER_ROLE.REVIEWER.TYPE);
        setUserCompanyId(result?.companyId);
        userId = result?.username;
        companyId = result?.companyId;
      } catch (err) {
        Sentry.captureException(err);
        console.log('Get user with role request failed with error', err);
      }
    }

    // Fetch user feature flags
    let userFeatures = [];
    if (userId && companyId) {
      try {
        const featuresResponse = await VormatsAPI.listUserFeatures(userId, companyId, { perPage: 100 });
        userFeatures = featuresResponse?.data || featuresResponse;
      } catch (err) {
        Sentry.captureException(err);
        console.log('Error fetching user feature flags', err);
      }
    }

    setUserFeatures(userFeatures);

    // Helper to get feature enabled state
    const isFeatureEnabled = name => {
      const feature = userFeatures.find(f => f.feature?.publicName === name || f.feature?.name === name);
      return feature ? Boolean(feature.enabled) : false;
    };

    setIsApprovalFlowEnabled(isFeatureEnabled('approval_flow'));
    setIsAppcuesEnabled(isFeatureEnabled('appcues'));
    setIsWebInvitationEnabled(isFeatureEnabled('web_invitation'));
    setIsStoryboardGeneratorEnabled(isFeatureEnabled('storyboard_generator'));
    setIsSceneSpeedEnabled(isFeatureEnabled('scene_speed'));
    setIsInviteCreateUser(isFeatureEnabled('invite_create_user'));
    setIsFirebaseEnabled(isFeatureEnabled('firebase'));
    if (isFeatureEnabled('firebase')) {
      initializeFirebase();
    }
    setIsCallToActionEnabled(isFeatureEnabled('web_cta'));
    setIsZoomFeatureEnabled(isFeatureEnabled('web_zoom'));
    setIsSentryEnabled(isFeatureEnabled('web_sentry'));
    setIsUserManagementEnabled(isFeatureEnabled('web_user_management'));
    setIsViewAnalyticsEnabled(isFeatureEnabled('view_analytics'));
    setIsLogoManagementEnabled(isFeatureEnabled('web_logo_management'));
    setIsTextPanelManagementEnabled(isFeatureEnabled('web_text_panel_management'));
    setIsVideoTagsEnabled(isFeatureEnabled('web_video_tags'));
    setIsProductTrainingEnabled(isFeatureEnabled('web_start_product_training'));
    setIsProfessionalRequestEnabled(isFeatureEnabled('professional_video_request'));
    setIsSSOAuthenticationEnabled(isFeatureEnabled('sso'));
    setIsMinutesModelEnabled(isFeatureEnabled('minute_model'));
    setIsStoryboardAssistantEnabled(isFeatureEnabled('storyboard_assistant'));
    setIsTypebotEnabled(isFeatureEnabled('web_type_bot'));
    setIsVoiceoverEnabled(isFeatureEnabled('voiceover'));
    setIsAudioEnhanceEnabled(isFeatureEnabled('audio_enhance'));
    setIsAvatarEnabled(isFeatureEnabled('avatar'));
    setIsCustomColorsEnabled(isFeatureEnabled('custom_colors'));
    setIsContentScheduleEnabled(isFeatureEnabled('content_schedule'));
    setIsVormatsPro(isFeatureEnabled('vormats_pro'));
    setIsImproveSubtitlesEnabled(isFeatureEnabled('improve_subtitles'));
    setIsGenerateVoiceoverEnabled(isFeatureEnabled('generate_voiceover'));
    setIsAdvancedEditorButtonEnabled(isFeatureEnabled('advanced_editor_button'));
    setIsScriptAssistantAttachmentsEnabled(isFeatureEnabled('script_assistant_attachments'));
    setIsStoryboardScriptPromptsV2Enabled(isFeatureEnabled('storyboard_script_prompts_v2'));
    setIsEditAutomaticallyStoryboardEnabled(isFeatureEnabled('edit_automatically_storyboard'));
    setIsWhatToShowVisibleWebEnabled(isFeatureEnabled('what_to_show_visible_web'));
    setIsNewGenerationFlow(isFeatureEnabled('new_home_generation_flow'));
    setNewWebAppDesign(isFeatureEnabled('new_web_app_design'));

    // Optionally, fetch appConfig for logos/fonts if needed
    try {
      const appConfigResponse = await CmsAPI.getAppConfig(userData?.username);
      const appConfigResult = appConfigResponse?.result;
      if (appConfigResult) {
        // Replace snake_case with camelCase for logo object keys
        appConfigResult.company_logos.forEach(logo => {
          for (const key in logo) {
            if (key.includes('_')) {
              const newKey = key.replace(/_([a-z])/g, g => g[1].toUpperCase());
              logo[newKey] = logo[key];
              delete logo[key];
            }
          }
        });
        setAppConfig(appConfigResult);
      }
    } catch (err) {
      // Ignore errors for logos/fonts
    }

    // Notification settings
    if (user !== null) {
      const settings = await CmsAPI.getNotificationSettings();
      if (settings) {
        setNotificationSettings(settings);
      }
    }
  };

  useEffect(() => {
    if (user !== null) {
      getAppConfig(user);
    }
  }, [user]);

  useEffect(() => {
    appConfig?.fonts?.forEach(async item => {
      try {
        const labelFont = new FontFace(item.name, `url(${item.font_url})`);
        const loadedFace = await labelFont.load();
        document.fonts.add(loadedFace);
      } catch (err) {
        console.log(err);
      }
    });
  }, [appConfig?.fonts]);

  useEffect(() => {
    fonts?.forEach(async item => {
      try {
        const labelFont = new FontFace(item.name, `url(${item.bucket})`);
        const loadedFace = await labelFont.load();
        document.fonts.add(loadedFace);
      } catch (err) {
        console.log(err);
      }
    });
  }, [fonts]);

  useEffect(() => {
    if (!pathname?.startsWith('/eg') || !user || !isWebAccessDisabled || hasForcedWebAccessLogout.current) {
      return;
    }

    hasForcedWebAccessLogout.current = true;
    history.push('/logout', { web_access_error: true });
  }, [history, isWebAccessDisabled, pathname, user]);

  useEffect(() => {
    if (!user || !isWebAccessDisabled) {
      hasForcedWebAccessLogout.current = false;
    }
  }, [isWebAccessDisabled, user]);

  const reset = () => {
    setUser(null);
    setAppConfig(null);
    setNotLoggedIn(false);
    setIsApprovalFlowEnabled(false);
    setIsAppcuesEnabled(false);
    setIsReviewerUser(false);
    setUserCompanyId(null);
    setDefaultSettings([]);
    setNotificationSettings([]);
    setIsWebInvitationEnabled(false);
    setIsStoryboardGeneratorEnabled(false);
    setIsSceneSpeedEnabled(false);
    setIsInviteCreateUser(false);
    setIsFirebaseEnabled(false);
    setIsCallToActionEnabled(false);
    setIsSentryEnabled(false);
    setIsZoomFeatureEnabled(false);
    setIsUserManagementEnabled(false);
    setIsViewAnalyticsEnabled(false);
    setIsLogoManagementEnabled(false);
    setIsSSOAuthenticationEnabled(false);
    setIsCustomColorsEnabled(false);
    setIsContentScheduleEnabled(false);
    setIsVormatsPro(false);
    setIsImproveSubtitlesEnabled(false);
    setIsGenerateVoiceoverEnabled(false);
    setIsAdvancedEditorButtonEnabled(false);
    setIsWhatToShowVisibleWebEnabled(false);
    setFonts([]);
  };

  const setUserInfo = useCallback(({ vormatsUser }) => {
    setUser(vormatsUser);
  }, []);

  const reloadUser = useCallback(async () => {
    const isLoggedIn = await CmsAPI.isLoggedIn();

    if (isLoggedIn) {
      const reloadedUser = await reloadUserFromCmsAPI();
      return reloadedUser;
    } else {
      setNotLoggedIn(true);
    }

    const userCacheInfo = JSON.parse(localStorage.getItem('vormatsUserData'));
    if (userCacheInfo) {
      setUser(userCacheInfo);
      setUserLoading(false);
    }

    return userCacheInfo;
  }, [setUser]);

  const reloadUserFromCmsAPI = async () => {
    try {
      const accessJwtToken = await CmsAPI.getAccessJwtToken();
      const idToken = jwtDecode(accessJwtToken);
      const cmsUser = await CmsAPI.getUser();
      console.log('cmsUser', cmsUser);
      const nameMissing = idToken.name === null || idToken.name === undefined;
      const nameParts = nameMissing ? ['', ''] : idToken.name.split(' ');
      const { userStats, settings } = cmsUser;
      const updatedUserData = {
        userEmail: idToken.email,
        firstName: parseString(nameParts[0]),
        lastName: nameParts.length > 1 ? parseString(nameParts.splice(1, nameParts.length - 1).join(' ')) : '',
        userFullName: nameMissing ? '' : parseString(idToken.name),
        shortName: Helper.generateShortName(nameMissing ? '' : idToken.name),
        avatarUri: cmsUser.avatar,
        onboarded: cmsUser.onboarded,
        userStats,
        settings,
        username: idToken['cognito:username'],
      };
      localStorage.setItem('vormatsUserData', JSON.stringify(updatedUserData));
      userLoading && setUserLoading(false);
      settings &&
        setDefaultSettings([
          { text: 'my_video.tools.preferences.download', setting: 'canDownload', value: settings['canDownload'] },
          { text: 'my_video.tools.preferences.comment', setting: 'canComment', value: settings['canComment'] },
          {
            text: 'my_video.tools.preferences.analytics',
            setting: 'canViewAnalytics',
            value: settings['canViewAnalytics'],
          },
          { text: 'my_video.tools.preferences.share', setting: 'canShare', value: settings['canShare'] },
          { text: 'my_video.tools.preferences.gif', setting: 'disableGif', value: settings['disableGif'] },
          {
            text: 'my_video.tools.preferences.share_player_only',
            setting: 'sharePlayerOnly',
            value: settings['sharePlayerOnly'],
          },
          { text: 'my_video.tools.preferences.show_sidebar', setting: 'showSidebar', value: settings['showSidebar'] },
          {
            text: 'my_video.tools.preferences.restrict_to_logged_in_users',
            setting: 'restrictToLoggedInUsers',
            value: settings['restrictToLoggedInUsers'] ?? false,
          },
        ]);
      setUser(updatedUserData);
      return updatedUserData;
    } catch (e) {
      console.log('cmsUser failed', e);
      history.push('/logout');
    }
  };

  const updateUserData = useCallback(async () => {
    try {
      console.log('updateUser function');
      const updatedData = await reloadUserFromCmsAPI();
      localStorage.setItem('vormatsUserData', JSON.stringify(updatedData));
      setUser(updatedData);
      return updatedData;
    } catch (error) {
      console.log('update user error', error);
      history.push('/logout');
    }
  }, [setUser]);

  const resetDefaultSettings = async () => {
    try {
      await CmsAPI.updateUserSetting({
        video_preferences: null,
      }).then(
        ToastNotification({
          message: t('my_video.tools.preferences.successfully_reset_settings'),
          iconName: 'success',
          isMobileView: isMobileView,
        })
      );
      reloadUserFromCmsAPI();
    } catch (err) {
      toast.error(`Unable to reset settings`);
    }
  };

  const toggleVideoSetting = async (settingKey, settingValue) => {
    setDefaultSettings(prevState => prevState.map(item => (item.setting === settingKey ? { ...item, value: settingValue } : item)));
    try {
      await CmsAPI.updateUserSetting({
        setting: settingKey,
        value: settingValue,
      });
      console.log(`${settingKey} state changed!`);
    } catch (err) {
      console.log('Err:', err);
      toast.error(`Unable to change ${settingKey} state.`);
    }
  };

  const changeSetting = async (settings, undoSetting) => {
    try {
      await CmsAPI.updateNotificationSettings({ settings });
      ToastNotification({
        message: t('account.settings.notifications.successfully_changed_setting'),
        iconName: 'success',
        isMobileView: isMobileView,
      });
    } catch (err) {
      undoSetting(); // Set toggle back, if change fails
      ToastNotification({
        message: t(`account.settings.notifications.unable_changed_setting`),
        isMobileView: isMobileView,
      });
    }
  };

  const logFirebaseEvent = useCallback(
    (eventName, eventParams) => {
      if (notLoggedIn) {
        initializeFirebase();
      }
      if ((notLoggedIn ? true : isFirebaseEnabled) && FirebaseFeature.analytics) {
        logEvent(FirebaseFeature.analytics, eventName, eventParams);
      }
    },
    [notLoggedIn, isFirebaseEnabled]
  );

  const getAppConfigForNotSignedInUser = videoOwnerData => {
    videoOwnerData && getAppConfig(videoOwnerData);
  };

  const updateUserAppConfig = () => {
    getAppConfig(user);
  };

  const checkFeatureEnabledAfterDate = useCallback((featureName, date) => {
    const feature = userFeatures.find(f => f.feature?.publicName === featureName || f.feature?.name === featureName);
    if (feature && feature.enabled && feature?.enabledOn) {
      const enabledDate = new Date(feature?.enabledOn);
      const targetDate = new Date(date);
      return targetDate.getTime() >= enabledDate.getTime();
    }
    return false;
  }, [userFeatures]);

  return useMemo(
    () => ({
      user,
      setUser,
      reset,
      setUserInfo,
      reloadUser,
      updateUserData,
      userLoading,
      toggleVideoSetting,
      resetDefaultSettings,
      defaultSettings,
      setDefaultSettings,
      appConfig,
      getAppConfigForNotSignedInUser,
      isApprovalFlowEnabled,
      isAppcuesEnabled,
      isReviewerUser,
      logFirebaseEvent,
      userCompanyId,
      notLoggedIn,
      notificationSettings,
      changeSetting,
      setNotificationSettings,
      isWebInvitationEnabled,
      isStoryboardGeneratorEnabled,
      isSceneSpeedEnabled,
      updateUserAppConfig,
      fonts,
      isCallToActionEnabled,
      isSentryEnabled,
      isInviteCreateUser,
      isZoomFeatureEnabled,
      isUserManagementEnabled,
      isViewAnalyticsEnabled,
      isLogoManagementEnabled,
      userRole,
      isSuperAdmin,
      isTextPanelManagementEnabled,
      isVideoTagsEnabled,
      isSSOAuthenticationEnabled,
      isProductTrainingEnabled,
      isProfessionalRequestEnabled,
      isMinutesModelEnabled,
      isStoryboardAssistantEnabled,
      companyTags,
      updateTags,
      isTypebotEnabled,
      isVoiceoverEnabled,
      isAudioEnhanceEnabled,
      isAvatarEnabled,
      isCustomColorsEnabled,
      isExternalVideoEditor,
      initializeFirebase,
      isContentScheduleEnabled,
      isVormatsPro,
      isImproveSubtitlesEnabled,
      isGenerateVoiceoverEnabled,
      isAdvancedEditorButtonEnabled,
      isScriptAssistantAttachmentsEnabled,
      isStoryboardScriptPromptsV2Enabled,
      isEditAutomaticallyStoryboardEnabled,
      isWhatToShowVisibleWebEnabled,
      isNewGenerationFlow,
      newWebAppDesign,
      userFeatures,
      checkFeatureEnabledAfterDate,
    }),
    [
      reloadUser,
      updateUserData,
      reset,
      setUserInfo,
      user,
      userLoading,
      defaultSettings,
      setDefaultSettings,
      isApprovalFlowEnabled,
      isAppcuesEnabled,
      isReviewerUser,
      logFirebaseEvent,
      appConfig,
      userCompanyId,
      notLoggedIn,
      notificationSettings,
      isWebInvitationEnabled,
      isStoryboardGeneratorEnabled,
      changeSetting,
      setNotificationSettings,
      isSceneSpeedEnabled,
      updateUserAppConfig,
      fonts,
      isCallToActionEnabled,
      isSentryEnabled,
      isInviteCreateUser,
      isZoomFeatureEnabled,
      isUserManagementEnabled,
      isViewAnalyticsEnabled,
      isLogoManagementEnabled,
      userRole,
      isSuperAdmin,
      isTextPanelManagementEnabled,
      isVideoTagsEnabled,
      isSSOAuthenticationEnabled,
      isProductTrainingEnabled,
      isProfessionalRequestEnabled,
      isMinutesModelEnabled,
      isStoryboardAssistantEnabled,
      companyTags,
      updateTags,
      isTypebotEnabled,
      isVoiceoverEnabled,
      isAudioEnhanceEnabled,
      isAvatarEnabled,
      isExternalVideoEditor,
      isCustomColorsEnabled,
      initializeFirebase,
      isContentScheduleEnabled,
      isVormatsPro,
      isImproveSubtitlesEnabled,
      isGenerateVoiceoverEnabled,
      isAdvancedEditorButtonEnabled,
      isScriptAssistantAttachmentsEnabled,
      isStoryboardScriptPromptsV2Enabled,
      isEditAutomaticallyStoryboardEnabled,
      isWhatToShowVisibleWebEnabled,
      isNewGenerationFlow,
      newWebAppDesign,
      userFeatures,
      checkFeatureEnabledAfterDate,
    ]
  );
}

export const useUserContext = constate(useUser);

const UserContext = {
  Provider: useUserContext.Provider,
};

export default UserContext;
